/**
 * @ken/module-customers — frontend entry point.
 *
 * Exports exactly what the cockpit shell expects per KEN_LAYER_7 §6.3:
 *   - createRoutes(parent) — factory returning the module's TanStack Router
 *                            children, mounted under the cockpit's tenant
 *                            parent route at boot.
 *
 * The manifest is NOT exported here (CV-19). modules/customers/manifest.yaml
 * is the only copy; the build reads it and hands the cockpit the parsed
 * object through the generated registry.
 *
 * 2026-06-15 (story_page_archetype_rollout): the in-list detail Sheet is
 * RETIRED for customers in favour of the full-page STORY route
 * (ADR-0081/0082, PARTY archetype). Clicking a customer row navigates to
 * `customers/$id/overview`; the parent `customers/$id` renders the
 * StoryPageDetail shell + routed tab children (overview / related / notes)
 * with an index redirect to overview. Mirrors the invoicing story rollout.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { CustomerStorySearch } from "./routes/story/customer-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadCustomerStoryTabs = () => import("./routes/story/customer-story-tabs");
const loadCustomerPeoplePlacesRoutes = () => import("./routes/story/customer-people-places-routes");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const CustomerCreatePage = lazyScreen(
  () => import("./routes/customer-create"),
  "CustomerCreatePage",
);
const CustomerListPage = lazyScreen(
  () => import("./routes/customer-list"),
  "CustomerListPage",
);
const CustomerStoryDetail = lazyScreen(
  () => import("./routes/story/customer-story-detail"),
  "CustomerStoryDetail",
);
const CustomerActivityRoute = lazyScreen(
  loadCustomerStoryTabs,
  "CustomerActivityRoute",
);
const CustomerContactsRoute = lazyScreen(
  loadCustomerPeoplePlacesRoutes,
  "CustomerContactsRoute",
);
const CustomerSitesRoute = lazyScreen(
  loadCustomerPeoplePlacesRoutes,
  "CustomerSitesRoute",
);
const CustomerNotesRoute = lazyScreen(
  loadCustomerStoryTabs,
  "CustomerNotesRoute",
);
const CustomerContributedTabRoute = lazyScreen(
  () => import("./routes/story/customer-contributed-tab-route"),
  "CustomerContributedTabRoute",
);
const CustomerOverviewRoute = lazyScreen(
  loadCustomerStoryTabs,
  "CustomerOverviewRoute",
);
const CustomerRelatedRoute = lazyScreen(
  loadCustomerStoryTabs,
  "CustomerRelatedRoute",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "customers",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (raw: Record<string, unknown>): CustomerListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: CustomerListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "customers/new",
    component: CustomerCreatePage,
  });

  // ── Story-page detail (ADR-0081): parent customers/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "customers/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): CustomerStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
      ...(typeof raw["price_level"] === "string" && raw["price_level"]
        ? { price_level: raw["price_level"] }
        : {}),
    }),
    component: CustomerStoryDetail,
  });

  // Index redirect customers/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/customers/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: CustomerOverviewRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: CustomerRelatedRoute,
  });
  // SS-B4-T1 (ADR-0277 point 3): the Invoices tab is Invoicing's own record
  // tab now, so "invoices" reaches the contributed-tab route below.
  // LOG-C (PRD 45, ADR-0260 point 8): the customer's own Contacts and Sites.
  const contactsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "contacts",
    component: CustomerContactsRoute,
  });
  const sitesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "sites",
    component: CustomerSitesRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: CustomerNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: CustomerActivityRoute,
  });

  // ADR-0251: a tab another module contributes. The story page renders its
  // body from the address; this route only answers an unknown key.
  const contributedTabRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "$contributedTab",
    component: CustomerContributedTabRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      relatedRoute,
      contactsRoute,
      sitesRoute,
      notesRoute,
      activityRoute,
      contributedTabRoute,
    ],
  );

  return [listRoute, createRouteEntry, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface CustomerListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
